import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../../context/ThemeContext';
import { Sun, Moon, LayoutDashboard, Rss } from 'lucide-react';
import useStatusData from '../hooks/useStatusData';
import { resolveAssetUrl } from '../../../shared/utils/branding';
import { parseServerTime } from '../../../shared/utils/format';
import {
  getOverallStatus,
  getStatusClass,
  getUptimeColorValue,
  formatUptimePercent,
  getStatusLabel,
  isMaintenanceActive,
  getDisplayStatus,
} from '../../../shared/utils/status';

import '../../../styles/base/App.css';
import '../../../styles/features/status/StatusPage.css';

import { IncidentHistory, IncidentBanner, MaintenanceNotice, MaintenanceHistory } from '../../incidents/components';
import { LoadingSpinner, Footer, UsageMeter } from '../../../shared/components';
import {
  UptimeHeatmap,
  UptimeBar,
  StatusPageCategories,
  ResponseTimeChart,
  UptimeStats,
  EndpointDetailView,
  StatusOverviewCard,
} from '../components';

function formatCheckTime(value) {
  return parseServerTime(value)?.toLocaleTimeString() || null;
}

function StatusPage({ socket }) {
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);

  const {
    endpoints,
    categories,
    incidents,
    maintenance,
    loading,
    error,
    endpointDetails,
    typeStats,
    pingStats,
    siteTitle,
    branding,
    endpointHistory,
  } = useStatusData(socket, selectedEndpoint);

  const { theme, toggleTheme } = useTheme();
  const { endpointId: urlEndpointId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (urlEndpointId) {
      const parsedId = parseInt(urlEndpointId, 10);
      setSelectedEndpoint(Number.isNaN(parsedId) ? null : parsedId);
    } else {
      setSelectedEndpoint(null);
    }
  }, [urlEndpointId]);

  const activeIncidents = incidents.filter((i) => !i.resolved_at);

  const incidentEndpointIds = new Set(
    activeIncidents.flatMap((i) => (i.endpoints || []).map((e) => e.id))
  );

  const currentOverallStatus = getOverallStatus(endpoints);

  const buildRecentEvents = (endpoint) => {
    if (!endpoint) return [];

    const checks = endpoint.results?.length
      ? endpoint.results
      : endpoint.latest
      ? [endpoint.latest]
      : [];

    return checks.slice(0, 12).map((check) => {
      const currentStatus = String(check.status || 'unknown').toLowerCase();

      return {
        status: currentStatus,
        label:
          currentStatus === 'up'
            ? 'Operational'
            : currentStatus === 'down'
            ? 'Down'
            : currentStatus === 'degraded'
            ? 'Degraded'
            : 'Unknown',
        time: formatCheckTime(check.checked_at) || 'Unknown time',
      };
    });
  };

  if (loading) {
    return (
      <div className="loading-page">
        <LoadingSpinner size="medium" text="Loading status data..." />
      </div>
    );
  }

  if (selectedEndpoint && endpointDetails) {
    const endpoint = endpointDetails;

    const endpointIncidents = incidents.filter((incident) =>
      (incident.endpoints || []).some((e) => e.id === endpoint.id)
    );

    const endpointMaintenances = maintenance.filter(
      (window) =>
        isMaintenanceActive(window) &&
        (window.endpoints || []).some((e) => e.id === endpoint.id)
    );

    const responseChart = <ResponseTimeChart endpointId={selectedEndpoint} />;
    const uptimeChart = <UptimeStats endpointId={selectedEndpoint} timeframe={30} />;
    const historyBar = <UptimeBar endpointId={selectedEndpoint} days={90} />;

    const recentEvents = buildRecentEvents(endpoint);

    const endpointUptime = {
      percentage:
        endpoint.uptime_30d != null
          ? Number(endpoint.uptime_30d)
          : null,
      totalChecks:
        endpoint.total_checks_30d ??
        endpoint.total_checks ??
        endpoint.totalChecks ??
        null,
      successfulChecks:
        endpoint.successful_checks_30d ??
        endpoint.successfulChecks ??
        null,
      downtimeEvents:
        endpoint.downtime_events_30d ??
        null,
    };

    const normalizedEndpoint = {
      ...endpoint,
      status: endpoint.in_maintenance ? 'maintenance' : (endpoint.latest?.status || endpoint.status || 'unknown'),
      responseTime: endpoint.latest?.response_time ?? endpoint.responseTime,
      lastChecked: formatCheckTime(endpoint.latest?.checked_at) || endpoint.lastChecked,
      lastCheckFormatted: formatCheckTime(endpoint.latest?.checked_at) || endpoint.lastCheckFormatted,
      checkInterval: endpoint.interval ?? endpoint.checkInterval,
      uptime30d: endpoint.uptime_30d,
      totalChecks: endpoint.total_checks_30d ?? endpoint.totalChecks,
    };

    return (
      <EndpointDetailView
        siteTitle={siteTitle}
        logoUrl={branding?.logoUrl ? resolveAssetUrl(branding.logoUrl) : ''}
        selectedEndpoint={normalizedEndpoint}
        endpointIncidents={endpointIncidents}
        endpointMaintenances={endpointMaintenances}
        checkStats={typeStats}
        pingStats={pingStats}
        uptime={endpointUptime}
        recentEvents={recentEvents}
        responseChart={responseChart}
        uptimeChart={uptimeChart}
        historyBar={historyBar}
        themeToggle={
          <button
            className="theme-toggle-btn"
            onClick={toggleTheme}
            type="button"
            aria-label="Toggle theme"
            title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        }
        onBack={() => {
          setSelectedEndpoint(null);
          navigate('/');
        }}
      />
    );
  }

  return (
    <div className="app status-page">
      <div className="container">
        <div className="header">
          <div className="header-brand">
            {branding?.logoUrl && (
              <img className="header-brand__logo" src={resolveAssetUrl(branding.logoUrl)} alt={siteTitle} />
            )}
            <div className="header-brand__text">
              <h1>{siteTitle}</h1>
              {branding?.tagline && <p className="header-brand__tagline">{branding.tagline}</p>}
            </div>
          </div>
          <div className="header-actions">
            <a
              href="/api/v1/feed.xml"
              className="theme-toggle-btn"
              title="Subscribe to incident feed"
              aria-label="Subscribe to incident feed"
            >
              <Rss size={16} />
            </a>

            <button
              onClick={toggleTheme}
              className="theme-toggle-btn"
              title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              type="button"
            >
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>

            <a href="/login" className="admin-link">
              <LayoutDashboard size={14} /> Admin
            </a>
          </div>
        </div>

        <StatusOverviewCard overallStatus={currentOverallStatus} />

        {error && <div className="error">{error}</div>}

        <MaintenanceNotice windows={maintenance} />
        <IncidentBanner incidents={activeIncidents} />

        {categories && categories.length > 0 ? (
          <>
            <p className="section-label">Services</p>
            <StatusPageCategories categories={categories} endpoints={endpoints} />
          </>
        ) : (
          <>
            <p className="section-label">Services</p>
            <div className="grid">
              {endpoints.map((endpoint) => {
                const status = getDisplayStatus(endpoint);
                  const uptime = formatUptimePercent(endpoint.uptime_30d, 1);

                return (
                  <div
                    key={endpoint.id}
                    className={`card service-card service-card--${status} service-card--clickable`}
                    onClick={() => navigate(`/endpoint/${endpoint.id}`)}
                  >
                    <div
                      className={`service-card__accent service-card__accent--${status}`}
                    />
                    <div className="service-card__body">
                      <div className="service-card__top">
                        <div className="service-card__title-group">
                          <h3 className="service-card__name">{endpoint.name}</h3>
                          <p className="service-url">{endpoint.url}</p>
                          <UsageMeter usage={endpoint.usage} compact />
                        </div>

                        <div className="service-card__badges">
                          {incidentEndpointIds.has(endpoint.id) && (
                            <span className="incident-impact-badge">Incident</span>
                          )}

                          {uptime && (
                            <span
                              className="uptime-30d-badge"
                              style={{ color: getUptimeColorValue(endpoint.uptime_30d) }}
                            >
                              {uptime}{' '}
                              <span className="uptime-30d-badge__period">30d</span>
                            </span>
                          )}

                          <span className={`status-badge ${getStatusClass(status)}`}>
                            {getStatusLabel(status)}
                          </span>
                        </div>
                      </div>

                      {endpoint.latest && (
                        <div className="service-card__meta">
                          <span className="service-meta-item">
                            <span className="service-meta-item__label">Response</span>
                            <strong>{endpoint.latest.response_time}ms</strong>
                          </span>

                          <span className="service-meta-item">
                            <span className="service-meta-item__label">Checked</span>
                            <strong>
                              {formatCheckTime(endpoint.latest.checked_at)}
                            </strong>
                          </span>
                        </div>
                      )}

                      <UptimeHeatmap
                        endpointId={endpoint.id}
                        days={90}
                        compact
                        history={endpointHistory[endpoint.id] || []}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {endpoints.length === 0 && !loading && (
          <div className="card empty-state">
            <div className="empty-state__icon">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
              </svg>
            </div>
            <p className="empty-state__text">No services configured yet.</p>
          </div>
        )}

        <div className="incident-history-section">
          <IncidentHistory incidents={incidents} />
          <MaintenanceHistory />
        </div>

        <Footer />
      </div>
    </div>
  );
}

export default StatusPage;
